import { Types } from "@grandvisionhq/graphql";
import { isDefined, getLocalizedString, isNumber } from "@grandvisionhq/utils";
import { createProductTypeName } from "../../utils/utils/create-product-type-name.js";
import { getConsolidationFacet } from "../../utils/utils/get-consolidation-facet.js";
import { getDiscount } from "../../utils/utils/get-discount.js";
const mapGraphProductToListingProduct = (product, intl, priceSegment) => {
  const { locale } = intl;
  const slug = product.slug;
  const productType = createProductTypeName(product.productType);
  const unmappedMasterVariant = product.variants[0];
  if (!slug || !productType || !product.variants || !unmappedMasterVariant) {
    console.error(
      `No slug, objectId, productType or variants found for product slug: ${product.slug}`
    );
    return void 0;
  }
  const masterVariant = mapVariant(product, unmappedMasterVariant, locale, priceSegment);
  if (!masterVariant) {
    console.error("No variants found for product slug: ", product.slug);
    return void 0;
  }
  const otherVariants = product.variants.slice(1).map((variant) => mapVariant(product, variant, locale, priceSegment)).filter(isDefined);
  const localizedName = getLocalizedString(product.nameAllLocales, locale);
  const isFrameProduct = product.__typename === "FrameProduct";
  const isOfferedWithPrescriptions = isFrameProduct || product.__typename === "SunglassesProduct" ? Boolean(product.productTypeDetails.isOfferedWithPrescriptions) : false;
  const includesLenses = isFrameProduct ? product.productTypeDetails.offerings?.find((offering) => offering.includesLenses)?.includesLenses ?? void 0 : void 0;
  const totalQuantity = unmappedMasterVariant.inventory?.totalQuantity ?? 0;
  return {
    masterVariant,
    objectID: "",
    // not available on the graph
    isOnStock: Boolean(totalQuantity >= 1),
    includedVatAmount: product.taxCategory?.rate?.amount,
    offering: { includesLenses },
    isOfferedWithPrescriptions,
    name: {
      en: product.name,
      localized: localizedName
    },
    ratingsSummary: {
      average: product.ratingsSummary?.average,
      reviewCount: product.ratingsSummary?.reviewCount
    },
    productType,
    slug,
    variants: otherVariants
  };
};
const mapVariant = (product, variant, locale, priceSegment) => {
  const images = variant.media.images.map((img) => ({
    url: img.url,
    label: img.label || "",
    altTag: "",
    dimensions: {
      w: img.dimensions.w,
      h: img.dimensions.h
    }
  }));
  const priceList = variant.priceList.filter(
    (priceListItem) => priceListItem.priceType === Types.ProductPriceItemType.SINGLE_PURCHASE
  );
  const singlePurchasePricesSegment = priceList.find(
    (price) => price.priceSegment?.channelKey === priceSegment
  );
  const singlePurchasePricesDefault = priceList.find((price) => !price.priceSegment?.channelKey);
  const prices = singlePurchasePricesSegment || singlePurchasePricesDefault;
  if (!prices || !prices.originalPrice) {
    console.error("No prices found for product sku: ", variant.sku);
    return void 0;
  }
  const isContactLensVariant = variant.__typename === "ContactLensVariant";
  const isSolutionVariant = variant.__typename === "SolutionVariant";
  const isAccessoryVariant = variant.__typename === "AccessoriesVariant";
  const isSunglassesVariant = variant.__typename === "SunglassesVariant";
  const isFrameVariant = variant.__typename === "FrameVariant";
  const isReadyReaderVariant = variant.__typename === "ReadyReaderVariant";
  const hasTargetGender = isFrameVariant || isAccessoryVariant || isSunglassesVariant;
  const hasMaterial = isContactLensVariant || isAccessoryVariant;
  const getFrameFeature = () => {
    if (isFrameVariant) {
      return variant.variantTypeDetails.frameFeatures;
    }
    if (isSunglassesVariant || isReadyReaderVariant) {
      return variant.variantTypeDetails.maybeFrameFeatures;
    }
    return void 0;
  };
  const getBrandModelAndCode = () => {
    if (isAccessoryVariant) {
      return {
        model: variant.variantTypeDetails.brandModel,
        code: variant.variantTypeDetails.brandModelCode
      };
    }
    if (isSunglassesVariant || isReadyReaderVariant || isFrameVariant) {
      return {
        model: variant.variantTypeDetails.maybeBrandModel,
        code: variant.variantTypeDetails.maybeBrandModelCode
      };
    }
    return void 0;
  };
  const consolidationFacet = getConsolidationFacet(variant);
  return {
    brand: {
      name: product.brand,
      model: getBrandModelAndCode()?.model || "",
      modelCode: getBrandModelAndCode()?.code || ""
    },
    description: variant.description || "",
    erpId: variant.erpId || "",
    name: variant.displayName,
    sku: variant.sku,
    frame: {
      color: {
        en: getFrameFeature()?.color.join(", ")
      },
      material: {
        en: getFrameFeature()?.material ?? void 0
      },
      shape: {
        en: getFrameFeature()?.shape?.displayName ?? void 0
      },
      type: {
        en: getFrameFeature()?.type ?? void 0
      }
    },
    material: hasMaterial ? variant.variantTypeDetails.material ?? void 0 : void 0,
    accessoryType: {
      en: isAccessoryVariant ? variant.variantTypeDetails.accessoryType?.displayName ?? void 0 : void 0
    },
    accessoryColor: {
      en: isAccessoryVariant ? variant.variantTypeDetails.color.join(", ") : void 0
    },
    lensType: {
      en: isContactLensVariant ? variant.variantTypeDetails.lensType ?? void 0 : void 0
    },
    faceShape: isFrameVariant || isSunglassesVariant ? { en: variant.variantTypeDetails.faceShape.map((val) => val.shape).join(", ") } : void 0,
    lensColor: isContactLensVariant ? variant.variantTypeDetails.lensColor ?? void 0 : void 0,
    priceIncludedAmount: void 0,
    // not available on the graph
    packageQuantity: isContactLensVariant ? variant.variantTypeDetails.packageQuantity ?? void 0 : void 0,
    solutionType: isSolutionVariant ? variant.variantTypeDetails.solutionType ?? void 0 : void 0,
    isExclusiveBrand: product.__typename === "FrameProduct" || product.__typename === "SunglassesProduct" ? product.productTypeDetails.isExclusiveBrand ?? void 0 : void 0,
    gender: {
      en: hasTargetGender ? variant.variantTypeDetails.targetGender.join(", ") : void 0
    },
    tag: variant.tags?.[0] ? {
      key: variant.tags[0].key,
      value: variant.tags[0].value
    } : void 0,
    images,
    power: isReadyReaderVariant ? variant.variantTypeDetails.power ?? void 0 : void 0,
    pricing: {
      // Best price is returned as a cent amount, but the other prices are floats, therefore, we transform it into a decimal
      bestPrice: isNumber(variant.bestPrice) ? variant.bestPrice / 100 : void 0,
      price: {
        isCentAmount: false,
        originalPrice: prices.originalPrice.amount,
        currencyCode: prices.originalPrice.currencyCode,
        discount: prices.discountedPrice?.price?.amount ? {
          price: prices.discountedPrice.price?.amount,
          badge: getDiscount(
            locale,
            prices.discountedPrice?.price?.currencyCode,
            prices.discountedPrice?.discountInformation?.type ?? void 0,
            prices.discountedPrice?.discountInformation?.value ?? void 0
          ),
          description: prices.discountedPrice.discountInformation?.description?.[0]?.value
        } : void 0
      },
      pricePerUnit: {
        amount: prices.pricePerUnit?.amount,
        currencyCode: prices.pricePerUnit?.currencyCode
      }
    },
    isInsuranceReimbursable: isFrameVariant && variant.variantTypeDetails.isInsuranceReimbursable,
    replacementFrequency: product.__typename === "ContactLensProduct" ? getLocalizedString(product.productTypeDetails.replacementFrequencyAllLocales, locale) : void 0,
    contactLensCareType: isSolutionVariant ? variant.variantTypeDetails.contactLensCareType || void 0 : void 0,
    packageOption: isSolutionVariant ? `${variant.variantTypeDetails.volume}${variant.variantTypeDetails.volumeUnit}` : void 0,
    consolidationFacet
  };
};
export {
  mapGraphProductToListingProduct,
  mapVariant
};
//# sourceMappingURL=map-graph-product-to-listing-product.js.map
